import { useEffect } from "react";
import { ArrowRight, Clock, CalendarDays } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { Reveal, Eyebrow } from "./Reveal";
import { ARTICLE_LIST, DOMAIN } from "../data/articles";
import { useLang } from "../i18n";

const DEFAULT_TITLE = "Impresa Edile a Bologna | Adriatica S.r.l. Semplificata";
const DEFAULT_DESC =
  "Adriatica S.r.l. Semplificata: impresa edile a Bologna con oltre 20 anni di esperienza. Ristrutturazioni, nuove costruzioni, impianti e lavori chiavi in mano.";
const GUIDE_TITLE = "Guide e Bonus Edilizi | Adriatica S.r.l. — Impresa Edile Bologna";
const GUIDE_DESC =
  "Guide pratiche su bonus ristrutturazioni, ecobonus ed efficientamento energetico e ristrutturazioni chiavi in mano. A cura di Adriatica S.r.l., impresa edile a Bologna.";

export default function GuidePage() {
  const { t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = GUIDE_TITLE;
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", GUIDE_DESC);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${DOMAIN}/guide`);
    return () => {
      document.title = DEFAULT_TITLE;
      if (md) md.setAttribute("content", DEFAULT_DESC);
      if (canonical) canonical.setAttribute("href", `${DOMAIN}/`);
    };
  }, []);

  return (
    <div className="grain bg-[#070D18] min-h-screen" data-testid="guide-page">
      <Navbar />
      <main>
        <section className="relative pt-40 pb-16 overflow-hidden">
          <div className="absolute inset-0 blueprint-dots opacity-[0.05] pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <Eyebrow>{t("Guide e bonus edilizi", "Guides & building bonuses")}</Eyebrow>
            <h1 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-3xl">
              {t("Guide pratiche", "Practical guides")}{" "}
              <span className="font-serif italic font-medium text-gold-gradient">
                {t("per costruire e ristrutturare", "for building and renovating")}
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              {t(
                "Detrazioni fiscali, efficientamento energetico e consigli da cantiere: quello che serve sapere prima di iniziare i lavori, spiegato in modo semplice.",
                "Tax deductions, energy efficiency and worksite advice: what you need to know before starting the works, explained simply."
              )}
            </p>
          </div>
        </section>

        <section className="relative pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="guide-articles-grid">
            {ARTICLE_LIST.map((a, i) => (
              <Reveal key={a.slug} delay={0.08 * (i % 3)}>
                <a href={`/guide/${a.slug}`} data-testid={`article-card-${a.slug}`} className="trace-card group block h-full rounded-2xl overflow-hidden bg-[#0E1726] hover:-translate-y-1.5 transition-transform">
                  <div className="relative h-48 overflow-hidden">
                    <img src={a.heroImg} alt={a.heroAlt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0E1726] to-transparent" />
                    <span className="absolute top-4 left-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#F3E08B] bg-[#070D18]/70 backdrop-blur-sm border border-[#D4AF37]/40 rounded-full px-3 py-1.5">
                      {a.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <h2 className="font-serif text-xl font-medium text-white leading-snug group-hover:text-[#F3E08B] transition-colors">
                      {a.title}
                    </h2>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{a.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} />{a.date}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={12} />{a.readTime}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#D4AF37]">
                        {t("Leggi", "Read")} <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
